import { useState } from "react";
import { Field, Module } from "../../../types/TenantInterfaces";
import { Button, Col, Collapse, Row } from "reactstrap";
import useToggle from "../../../hooks/useToggle";
import StrUtils from "../../../utils/StrUtils";
import { RecordSummaryWidget } from "../../Widgets/RecordSummaryWidget";
import SummaryCardToolbar from "../../Widgets/SummaryCardWidget/SummaryCardToolbar";
import TabbedMultiSubitem from "./TabbedMultiSubitem";
import AppTooltip from "../../../elements/AppTooltip";
import MultiSubitemEditor from "./MultiSubitemEditor";
import pluralize from "pluralize";
import { generatePrefill } from "../../../utils/QuickCreateUtils";
import useUser from "../../../api/api-hooks/auth/useUser";
import { ReusableViewReferenceFields } from "../../../types/PageInterfaces";

interface Props {
  module: Module;
  createMode: boolean;
  readOnly: boolean;
  values: any;
  section: any;
  parentRecord: any | undefined;
  fieldSchema: Field;
  record: any;
  prefill: any;
  handleChange: (data: any, newData: any, flag: boolean) => void;
  addSubitem: (data: any, moreData: any) => void;
  removeSubitem: (parentField: any, activeIndex: number) => void;
  convertFields: (field: any) => any;
  publicForm?: boolean;
  reusableViewReferenceFields?: ReusableViewReferenceFields;
}

const ManageMultiSubitem = ({
  convertFields,
  createMode,
  readOnly,
  values,
  module,
  parentRecord,
  section,
  fieldSchema,
  record,
  prefill,
  handleChange,
  publicForm = false,
  reusableViewReferenceFields,
}: Props) => {
  const { user } = useUser();
  const [collapse, toggleCollapse] = useToggle();
  const [subitemAdding, setSubitemAdding] = useState(false);
  const [isDuplicating, toggleSubitemDuplicate] = useToggle();
  const [viewType, setViewType] = useState<"Card" | "Table">("Table");
  const [activeIndex, setActiveIndex] = useState(0);
  const [selectedPrefill, setPrefill] = useState(prefill);

  return (
    <div className="mt-2 mb-4">
      {/* @ts-ignore */}
      <SummaryCardToolbar
        title={fieldSchema?.Name}
        description={fieldSchema?.Description}
        fieldSchema={fieldSchema}
        onEdit={null}
        collapse={collapse}
        reusableViewReferenceFields={reusableViewReferenceFields}
        customOptions={
          <>
            {viewType === "Card" && !subitemAdding && (
              <Button
                className="btn-icon-only"
                size="sm"
                color="primary"
                style={{ height: 23, width: 24 }}
                onClick={() => setSubitemAdding(true)}
              >
                <i className="mdi mdi-plus" style={{ top: 0, left: 0 }} />
              </Button>
            )}
            {subitemAdding || isDuplicating ? (
              <Button
                onClick={() => {
                  if (subitemAdding) setSubitemAdding(false);
                  if (isDuplicating) {
                    toggleSubitemDuplicate();
                    setPrefill(null);
                  }
                }}
                outline
                size="sm"
              >
                Done
              </Button>
            ) : (
              <AppTooltip
                useTooltip
                enterable={false}
                content={
                  viewType === "Card" ? "View as Table" : "View as Cards"
                }
              >
                <Button
                  className="btn-icon-only"
                  size="sm"
                  style={{ height: 23, width: 30 }}
                  outline
                  onClick={() =>
                    setViewType((prev) => (prev === "Card" ? "Table" : "Card"))
                  }
                >
                  <i
                    className={
                      viewType === "Card"
                        ? "mdi mdi-table-large"
                        : "mdi mdi-card-multiple-outline"
                    }
                    style={{ top: 0, left: 0 }}
                  />
                </Button>
              </AppTooltip>
            )}
          </>
        }
        toggleCollapse={toggleCollapse}
      />
      <Collapse className="m-0 p-0" isOpen={!collapse}>
        <div className="bg-ultralight btn-oauth rounded-sm">
          <div className="p-2">
            {subitemAdding || isDuplicating ? (
              <MultiSubitemEditor
                module={module}
                parentRecord={parentRecord}
                fieldSchema={fieldSchema}
                createMode={true}
                onSaveCreate={handleSaveSubitem}
                prefill={selectedPrefill}
                onDeleteCreate={handleDeleteSubitem}
                isDuplicating={isDuplicating}
                setPrefill={setPrefill}
                toggleDuplicateSubitem={toggleSubitemDuplicate}
                setSubitemAdding={setSubitemAdding}
                reusableViewReferenceFields={reusableViewReferenceFields}
              />
            ) : (
              <>
                {viewType === "Table" ? (
                  <>
                    <TabbedMultiSubitem
                      showTitle={false}
                      fieldSchema={fieldSchema}
                      records={values}
                      subitemAdding={subitemAdding}
                      parentRecord={parentRecord}
                      setAddingSubitem={setSubitemAdding}
                      createMode={true}
                      setSubitems={(newValues) =>
                        handleChange(section.ParentField, [...newValues], false)
                      }
                      prefills={selectedPrefill}
                      managePrefill={managePrefill}
                      module={module}
                      handleChange={(
                        field: any,
                        value: any,
                        recordID: string
                      ) => {
                        const newValues = [...values];
                        newValues.find((v) => v.id === recordID)[field] = value;
                        handleChange(
                          section.ParentField,
                          [...newValues],
                          false
                        );
                      }}
                      subitemDuplicating={isDuplicating}
                      toggleDuplicateSubitem={toggleSubitemDuplicate}
                      publicForm={publicForm}
                      reusableViewReferenceFields={reusableViewReferenceFields}
                    />
                  </>
                ) : (
                  <>
                    {values.length > 0 ? (
                      <>
                        {/* @ts-ignore */}
                        <RecordSummaryWidget
                          parentRecord={parentRecord}
                          module={module}
                          section={section}
                          convertFields={convertFields}
                          values={values[activeIndex] || null}
                          createMode={values.length > 0 ? true : false}
                          handleChange={(field: any, val: any) => {
                            const newValues = [...values];
                            newValues[activeIndex][field.APIName] = val;
                            handleChange(
                              section.ParentField,
                              [...newValues],
                              false
                            );
                          }}
                          record={record}
                          readOnly={readOnly}
                          reusableViewReferenceFields={
                            reusableViewReferenceFields
                          }
                        />
                      </>
                    ) : (
                      <div className="text-dark bg-lighter py-4 px-5 rounded-bottom font-weight-500">
                        {`No ${pluralize(fieldSchema.Name)} have been added.`}
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </Collapse>

      {viewType === "Card" && values.length > 1 && (
        <>
          <div className="pb-4 mt-3">
            <Row className="align-items-center">
              <Col xs="auto">
                <Button
                  size="sm"
                  color="dark"
                  outline
                  className="btn-icon-only "
                  style={{ height: 25, width: 25 }}
                  onClick={() => switchPage(activeIndex - 1)}
                >
                  <i
                    className="mdi mdi-chevron-left"
                    style={{
                      fontSize: 23,
                      lineHeight: 1,
                      position: "relative",
                      top: 1,
                    }}
                  />
                </Button>
              </Col>
              <Col>
                <div className="text-center">
                  <small style={{ fontSize: 14 }} className="font-weight-500">
                    {StrUtils.formatNumber(activeIndex + 1)} of{" "}
                    {StrUtils.formatNumber(values?.length)}
                  </small>
                </div>
              </Col>
              <Col xs="auto">
                <Button
                  size="sm"
                  color="dark"
                  outline
                  className="btn-icon-only"
                  style={{ height: 25, width: 25 }}
                  onClick={() => switchPage(activeIndex + 1)}
                >
                  <i
                    className="mdi mdi-chevron-right"
                    style={{
                      fontSize: 23,
                      lineHeight: 1,
                      position: "relative",
                      top: 1,
                    }}
                  />
                </Button>
              </Col>
            </Row>
          </div>
        </>
      )}
    </div>
  );

  function handleSaveSubitem(newValues: any) {
    if (createMode) {
      const updatedSubitems = [...values];
      const valuesIDs = Array.isArray(values)
        ? values.map((v: any) => v.id)
        : [];

      newValues.forEach((val: any) => {
        if (!valuesIDs.includes(val.id)) updatedSubitems.push(val);
      });
      return handleChange(section.ParentField, updatedSubitems, false);
    }

    return handleChange(section.ParentField, newValues, false);
  }

  function handleDeleteSubitem(itemID: string) {
    const filtered = values.filter((vals: any) => vals.id !== itemID);
    return handleChange(section.ParentField, filtered, false);
  }

  function switchPage(newPage: number) {
    if (newPage < 0) return setActiveIndex(values.length - 1);
    if (newPage >= values.length) return setActiveIndex(0);

    setActiveIndex(newPage);
  }

  function managePrefill(value: any) {
    const variableVals = {
      user: {
        id: user?.id,
        _RecordName: user?.Name,
        FirstName: StrUtils.getFirstName(user?.Name),
        LastName: StrUtils.getLastName(user?.Name),
        ModuleAPIName: "Contacts",
      },
    };
    const prefill = value.PrefillPairs;
    generatePrefill(
      prefill,
      variableVals,
      fieldSchema.SubitemFields,
      setPrefill
    );
  }
};

export default ManageMultiSubitem;
